import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ChevronDown, Smartphone, ShieldCheck, MessageSquare } from "lucide-react";
import { BUSINESS } from "@/lib/sleek";
import AppStoreBadge from "@/components/AppStoreBadge";

const FAQS = [
  {
    q: "How do I sign in to the customer portal?",
    a: "Open the customer portal app (or the portal link we emailed you) and sign in with the email address you gave us during your consultation. If it's your first time, use the invitation link in your welcome email to set a password.",
  },
  {
    q: "How do I request a quote?",
    a: "Submit the contact form on this site, book a consultation through our Booking page, or call us. We'll follow up within one business day to discuss your project and schedule an estimate.",
  },
  {
    q: "How do I reset my portal access?",
    a: "On the portal sign-in screen, tap \"Forgot password\" and follow the email instructions. If you no longer have access to that email address, contact us and we'll re-send your invitation.",
  },
  {
    q: "Where can I see my project progress?",
    a: "Sign in to the customer portal — your project dashboard shows the current phase, schedule, photos, and updates from our team as work progresses.",
  },
  {
    q: "How do I upload photos?",
    a: "In the customer portal, open your project and use the photo upload option to share inspiration images or site photos. You can also email them to us directly.",
  },
  {
    q: "Where do I sign my estimate?",
    a: "Estimates are delivered through the customer portal — open the estimate from your project dashboard and follow the prompt to review and sign electronically. We'll confirm by email once it's signed.",
  },
];

export default function Support() {
  const [open, setOpen] = useState(0);

  return (
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-24" data-testid="support-page">
      <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Support</div>
      <h1 className="font-display text-5xl md:text-6xl font-light tracking-tight mt-4 leading-[1.05]">
        Support &amp; Help — Sleek Renovations
      </h1>
      <p className="text-zinc-600 mt-6 leading-relaxed max-w-2xl">
        Questions about a project, your customer portal, an appointment — or anything else? Reach the{" "}
        {BUSINESS.remodel} and {BUSINESS.showroom} team directly, or start with the answers below.
      </p>

      {/* Contact Us */}
      <div className="mt-14">
        <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Contact us</div>
        <div className="grid md:grid-cols-3 gap-6 mt-6">
          <a href={BUSINESS.emailHref} className="border border-zinc-200 p-6 hover:border-zinc-950 transition group" data-testid="support-email-card">
            <Mail size={18} className="text-zinc-500 group-hover:text-zinc-950 transition" />
            <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 mt-4">Email</div>
            <div className="font-display text-xl mt-1 break-all">{BUSINESS.email}</div>
          </a>
          <a href={BUSINESS.phoneHref} className="border border-zinc-200 p-6 hover:border-zinc-950 transition group" data-testid="support-phone-card">
            <Phone size={18} className="text-zinc-500 group-hover:text-zinc-950 transition" />
            <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 mt-4">Phone</div>
            <div className="font-display text-xl mt-1">{BUSINESS.phone}</div>
          </a>
          <div className="border border-zinc-200 p-6" data-testid="support-hours-card">
            <Clock size={18} className="text-zinc-500" />
            <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 mt-4">Business hours</div>
            <div className="text-sm text-zinc-700 mt-2 leading-relaxed">
              Mon–Fri · 8:00 AM – 5:00 PM ET
              <br />
              Showroom · By Appointment Only
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3 text-zinc-600 text-sm mt-6" data-testid="support-address">
          <MapPin size={16} className="mt-0.5 shrink-0" />
          <span>
            {BUSINESS.showroom} · {BUSINESS.address} ·{" "}
            <a href={BUSINESS.mapsHref} target="_blank" rel="noopener noreferrer" className="underline hover:text-zinc-950">
              Get directions
            </a>
          </span>
        </div>
      </div>

      {/* How to reach us */}
      <div className="mt-14 border border-zinc-200 p-6 md:p-8 flex items-start gap-4" data-testid="support-response-times">
        <MessageSquare size={18} className="text-zinc-500 mt-1 shrink-0" />
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">How to reach us</div>
          <p className="text-sm text-zinc-700 mt-3 leading-relaxed">
            <b>Email:</b> we respond to every message within 24 hours.
            <br />
            <b>Phone:</b> call during business hours, or leave a voicemail anytime — we return calls the same business day.
          </p>
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-14 max-w-3xl">
        <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Common questions</div>
        <div className="mt-6 border-t border-zinc-200" data-testid="support-faq">
          {FAQS.map((f, i) => (
            <div key={f.q} className="border-b border-zinc-200">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-start justify-between gap-4 py-5 text-left"
                data-testid={`support-faq-toggle-${i}`}
                aria-expanded={open === i}
              >
                <span className="font-medium text-zinc-900">{f.q}</span>
                <ChevronDown size={16} className={`mt-1 shrink-0 text-zinc-500 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <p className="text-zinc-600 text-sm leading-relaxed pb-6 pr-8" data-testid={`support-faq-answer-${i}`}>
                  {f.a}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Privacy & Data */}
      <div className="mt-14 border border-zinc-200 p-6 md:p-8 flex items-start gap-4" data-testid="support-privacy">
        <ShieldCheck size={18} className="text-zinc-500 mt-1 shrink-0" />
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Privacy &amp; data</div>
          <p className="text-sm text-zinc-700 mt-3 leading-relaxed">
            We collect only the information needed to respond to your inquiries and manage your project — we never
            sell your personal data. For full details on what we collect, how we use it, and your rights, read our{" "}
            <Link to="/privacy" className="underline hover:text-zinc-950" data-testid="support-privacy-link">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>

      {/* App Support */}
      <div className="mt-6 border border-zinc-200 p-6 md:p-8 flex items-start gap-4" data-testid="support-app">
        <Smartphone size={18} className="text-zinc-500 mt-1 shrink-0" />
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">App support</div>
          <p className="text-sm text-zinc-700 mt-3 leading-relaxed">
            For iOS app issues, email{" "}
            <a href={BUSINESS.emailHref} className="underline hover:text-zinc-950">{BUSINESS.email}</a>{" "}
            with your device model, iOS version, and a description of the issue. We respond within 24 hours.
          </p>
          <div className="mt-4">
            <AppStoreBadge testid="support-app-store-badge" />
          </div>
        </div>
      </div>
    </div>
  );
}
